/* eslint-disable @typescript-eslint/no-explicit-any */

import { useCallback, useEffect, useRef, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Send } from "lucide-react";
import PhotoUploadButton from "@/components/PhotoUploadButton";
import FullScreenImage from "@/components/FullScreenImage";
import MentionComposerField from "@/components/MentionComposerField";
import { dispatchMentionNotifications, searchAllMentionSuggestions } from "@/lib/mentions";
import { resolveSignedStorageUrls, resolveStoredObjectUrl } from "@/lib/storageObjectUrls";
import { getFirstUnreadMessageId, type MentionDraft } from "../../../shared/community";
import { AppButton, AppButtonRow, AppCard, AppCardBody, AppSection, AppSectionHeader, AppText } from "../../../ui-foundation";

interface Message {
  id: string;
  content: string;
  created_at: string;
  user_id: string;
  photo_url?: string | null;
  profiles?: { display_name: string | null; avatar_url: string | null } | null;
}

const AdventureChat = ({
  adventureId,
  accessStartedAt,
}: {
  adventureId: string;
  accessStartedAt?: string | null;
}) => {
  const { user } = useAuth();
  const [messages, setMessages] = useState<Message[]>([]);
  const [newMessage, setNewMessage] = useState("");
  const [newMessageMentions, setNewMessageMentions] = useState<MentionDraft[]>([]);
  const [photoUrl, setPhotoUrl] = useState<string | null>(null);
  const [sending, setSending] = useState(false);
  const [fullScreenImage, setFullScreenImage] = useState<string | null>(null);
  const [resolvedPhotoUrls, setResolvedPhotoUrls] = useState<Map<string, string>>(new Map());
  const [firstUnreadMessageId, setFirstUnreadMessageId] = useState<string | null>(null);
  const messageListRef = useRef<HTMLDivElement>(null);
  const messageRefs = useRef(new Map<string, HTMLDivElement>());
  const initialPositionResolvedRef = useRef(false);
  const initialPositionAppliedRef = useRef(false);
  const shouldFollowMessagesRef = useRef(true);

  const markRead = useCallback(async () => {
    if (!user?.id) return;

    const now = new Date().toISOString();
    await supabase.from("adventure_chat_reads").upsert({
      adventure_id: adventureId,
      user_id: user.id,
      joined_at: accessStartedAt ?? now,
      last_read_at: now,
    });
  }, [accessStartedAt, adventureId, user?.id]);

  useEffect(() => {
    initialPositionResolvedRef.current = false;
    initialPositionAppliedRef.current = false;
    shouldFollowMessagesRef.current = true;
    messageRefs.current.clear();
    setFirstUnreadMessageId(null);
  }, [adventureId]);

  useEffect(() => {
    const fetchMessages = async () => {
      const [{ data }, { data: readMarker }] = await Promise.all([
        supabase
          .from("adventure_messages")
          .select("*, profiles(display_name, avatar_url)")
          .eq("adventure_id", adventureId)
          .order("created_at", { ascending: true }),
        user?.id
          ? supabase
              .from("adventure_chat_reads")
              .select("last_read_at")
              .eq("adventure_id", adventureId)
              .eq("user_id", user.id)
              .maybeSingle()
          : Promise.resolve({ data: null }),
      ]);
      if (data) {
        const nextMessages = data as unknown as Message[];
        if (!initialPositionResolvedRef.current) {
          setFirstUnreadMessageId(
            getFirstUnreadMessageId(
              nextMessages,
              user?.id,
              accessStartedAt,
              readMarker?.last_read_at,
            ),
          );
          initialPositionResolvedRef.current = true;
        }
        setMessages(nextMessages);
        await markRead();
      }
    };
    void fetchMessages();

    const channel = supabase
      .channel(`adventure-chat-${adventureId}`)
      .on("postgres_changes", {
        event: "INSERT", schema: "public", table: "adventure_messages",
        filter: `adventure_id=eq.${adventureId}`,
      }, async (payload) => {
        const list = messageListRef.current;
        shouldFollowMessagesRef.current = list
          ? list.scrollHeight - list.scrollTop - list.clientHeight < 80
          : true;
        const { data: profile } = await supabase.from("profiles").select("display_name, avatar_url").eq("id", (payload.new as any).user_id).single();
        setMessages((prev) => [...prev, { ...(payload.new as any), profiles: profile }]);
        await markRead();
      })
      .subscribe();

    return () => { supabase.removeChannel(channel); };
  }, [accessStartedAt, adventureId, markRead, user?.id]);

  useEffect(() => {
    const list = messageListRef.current;
    if (!list || !initialPositionResolvedRef.current) {
      return;
    }

    if (!initialPositionAppliedRef.current) {
      const target = firstUnreadMessageId
        ? messageRefs.current.get(firstUnreadMessageId)
        : null;
      list.scrollTop = target
        ? Math.max(0, target.offsetTop - list.offsetTop - 16)
        : list.scrollHeight;
      initialPositionAppliedRef.current = true;
      shouldFollowMessagesRef.current = true;
      return;
    }

    if (shouldFollowMessagesRef.current) {
      list.scrollTo({ top: list.scrollHeight, behavior: "smooth" });
    }
  }, [firstUnreadMessageId, messages]);

  useEffect(() => {
    let active = true;
    const photoValues = messages.map((message) => message.photo_url).filter(Boolean);

    void resolveSignedStorageUrls("adventure-photos", photoValues).then((nextMap) => {
      if (active) {
        setResolvedPhotoUrls(nextMap);
      }
    });

    return () => {
      active = false;
    };
  }, [messages]);

  const send = async () => {
    if ((!newMessage.trim() && !photoUrl) || !user || sending) return;
    const content = newMessage.trim();
    setSending(true);
    shouldFollowMessagesRef.current = true;
    const { error } = await supabase.from("adventure_messages").insert({
      adventure_id: adventureId,
      user_id: user.id,
      content: content || "📷",
      photo_url: photoUrl,
    } as any);

    if (error) {
      console.error("Could not send activity chat message", error);
      setSending(false);
      return;
    }

    await dispatchMentionNotifications({
      actorId: user.id,
      actorDisplayName:
        user.user_metadata?.display_name ?? user.email?.split("@")[0] ?? "Someone",
      content,
      mentions: newMessageMentions,
      bodyContext: "in an activity chat",
      entityType: "adventure",
      entityId: adventureId,
      url: `/adventures/${adventureId}/chat`,
    });
    setNewMessage("");
    setNewMessageMentions([]);
    setPhotoUrl(null);
    setSending(false);
  };

  return (
    <AppSection gap="$md">
      <AppSectionHeader title="Adventure Chat" subtitle="Keep the activity conversation in one calm thread." />
      <AppCard backgroundColor="$background">
        <AppCardBody gap="$md">
          <div
            ref={messageListRef}
            className="max-h-64 space-y-3 overflow-y-auto"
            onScroll={(event) => {
              const list = event.currentTarget;
              shouldFollowMessagesRef.current =
                list.scrollHeight - list.scrollTop - list.clientHeight < 80;
            }}
          >
            {messages.length === 0 && (
              <AppText color="$colorMuted" fontSize="$sm" lineHeight="$md" textAlign="center" py="$lg">
                No messages yet — say hello!
              </AppText>
            )}
            {messages.map((msg) => (
              <div
                key={msg.id}
                ref={(node) => {
                  if (node) {
                    messageRefs.current.set(msg.id, node);
                  } else {
                    messageRefs.current.delete(msg.id);
                  }
                }}
                className="space-y-2"
              >
                {msg.id === firstUnreadMessageId ? (
                  <div className="flex items-center gap-3 py-1" aria-label="New messages start here">
                    <span className="h-px flex-1 bg-primary/30" />
                    <span className="text-xs font-semibold text-primary">New messages</span>
                    <span className="h-px flex-1 bg-primary/30" />
                  </div>
                ) : null}
                <div className="flex gap-2">
                <Avatar className="h-7 w-7 shrink-0">
                  {msg.profiles?.avatar_url && <AvatarImage src={msg.profiles.avatar_url} />}
                  <AvatarFallback className="text-xs bg-secondary text-secondary-foreground">
                    {(msg.profiles?.display_name ?? "K")[0]}
                  </AvatarFallback>
                </Avatar>
                <div className="min-w-0">
                  <div className="flex items-baseline gap-2">
                    <AppText className="font-medium" fontSize="$xs" lineHeight="$xs">
                      {msg.profiles?.display_name ?? "Member"}
                    </AppText>
                    <AppText color="$colorMuted" fontSize={10} lineHeight={10}>
                      {new Date(msg.created_at).toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" })}
                    </AppText>
                  </div>
                  <AppText fontSize="$sm" lineHeight="$md">{msg.content}</AppText>
                  {msg.photo_url && resolveStoredObjectUrl("adventure-photos", msg.photo_url, resolvedPhotoUrls) ? (
                    <button
                      onClick={() => setFullScreenImage(resolveStoredObjectUrl("adventure-photos", msg.photo_url, resolvedPhotoUrls))}
                      className="mt-1 max-w-[160px] rounded-lg overflow-hidden"
                    >
                      <img src={resolveStoredObjectUrl("adventure-photos", msg.photo_url, resolvedPhotoUrls) ?? ""} alt="" className="w-full rounded-lg" />
                    </button>
                  ) : null}
                </div>
                </div>
              </div>
            ))}
          </div>
        </AppCardBody>
      </AppCard>
      <PhotoUploadButton
        bucket="adventure-photos"
        onPhotoReady={setPhotoUrl}
        onClear={() => setPhotoUrl(null)}
        previewUrl={photoUrl}
        returnObjectPath
      />
      <AppButtonRow flexWrap="nowrap">
        <MentionComposerField
          value={newMessage}
          onChangeText={setNewMessage}
          mentions={newMessageMentions}
          onMentionsChange={setNewMessageMentions}
          lookupCandidates={(query) => searchAllMentionSuggestions(query, user?.id)}
          placeholder="Type a message..."
          disabled={sending}
          fieldProps={{ flex: 1 }}
          inputProps={{
            flex: 1,
            onKeyDown: (event: any) => {
              if (event.key === "Enter") {
                event.preventDefault();
                void send();
              }
            },
          }}
        />
        <AppButton type="button" size="sm" onPress={() => void send()} disabled={sending || (!newMessage.trim() && !photoUrl)}>
          <Send size={16} />
          {sending ? "Sending..." : "Send"}
        </AppButton>
      </AppButtonRow>
      {fullScreenImage && (
        <FullScreenImage images={[fullScreenImage]} currentIndex={0} onClose={() => setFullScreenImage(null)} onNavigate={() => {}} />
      )}
    </AppSection>
  );
};

export default AdventureChat;
